import { getDatadogLogs } from './datadogLogs';

/**
 * The two ways a user ends up looking at a broken screen: a React subtree that threw, and a
 * microfrontend that never mounted (single-spa replaces the view with the generic error page).
 */
export type CrashSource = 'react_error_boundary' | 'microfrontend_mount';

/**
 * Reports a crash the user can see, tagged so it can be monitored on its own.
 *
 * `ui_crash` exists because `status:error` is not a usable signal: `forwardConsoleLogs` turns every
 * `console.error` — ours and every dependency's — into an error log, and `forwardErrorsToLogs` adds
 * every uncaught error and rejection. A monitor on status alone fires on all of it.
 *
 *   Monitor:  logs("@ui_crash:true service:(mobile-app OR web-app)")
 *
 * The marker is an *attribute* rather than a sentinel in `error.name` or the message, which is what
 * the previous `ReactRenderingError` wrapper did — that broke Error Tracking (a unique message per
 * report, so one issue per occurrence) and stopped our source maps applying. The error is passed
 * through untouched; only the log carries the tag.
 *
 * Rollout: released native clients keep sending the old shape until they age out, so the monitor
 * wants `@ui_crash:true OR ReactRenderingError` until then, and only the first clause after.
 */
export const reportUiCrash = (source: CrashSource, message: string, error: Error, context: object = {}) => {
  const datadogLogs = getDatadogLogs();

  if (!datadogLogs) {
    console.error(message, error, context);
    return;
  }

  datadogLogs.logger.error(
    message,
    {
      // The SDK omits source_type, and Error Tracking needs it to apply our source maps.
      error: { source_type: 'browser' },
      ui_crash: true,
      crash_source: source,
      ...context
    },
    error
  );
};

/** A microfrontend that failed to load or mount — single-spa's own error handler. */
export const logMicrofrontendError = (error: Error, context?: object) =>
  reportUiCrash('microfrontend_mount', error.message, error, context);
